// src/app/modules/user/components/charts/BloodPressureChart.tsx

import React, {useMemo, useEffect, useState} from 'react'
import axios from 'axios'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from '../../../../config/highcharts-setup'
import {TooltipFormatterCallbackFunction} from 'highcharts'
import {format} from 'date-fns'
import {useReduxAuth} from '../../../auth'
import {useParams} from 'react-router-dom'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import helperFunctions from '../../../../helpers/helpers'

interface Reading {
  sys: number
  dia: number
  pulse: number
  reading_taken_at: string
}

interface BloodPressureChartProps {
  data: Reading[]
}

interface Thresholds {
  sysMin: number
  sysMax: number
  diaMin: number
  diaMax: number
}

export const tooltipFormatter: TooltipFormatterCallbackFunction = function () {
  const pt = (this as any).point
  const readings: Reading[] = pt.options.readings ?? []
  const dayUTC: number = pt.options.bucketDay
  const header = Highcharts.dateFormat('%A, %b %e, %Y', dayUTC)

  return `
    <div style="min-width:140px">
      <div style="font-weight:600;margin-bottom:6px">${header}</div>
      <ul style="padding:0;margin:8px 0;list-style:none">
        ${readings
          .map((r) => {
            const timeStr = format(new Date(r.reading_taken_at), 'HH:mm')
            return `<li style="display:flex;align-items:center;margin-bottom:6px;line-height:1.4">
              <span style="width:38px;text-align:right;margin-right:8px">${timeStr}</span>
              <span style="width:84px;text-align:right"><strong>${r.sys}/${r.dia} mmHg</strong></span>
              <span style="width:10px"></span>
              <span style="width:38px;text-align:right;margin-left:8px"><strong>${r.pulse} bpm</strong></span>
            </li>`
          })
          .join('')}
      </ul>
    </div>
  `
}

// 30-day span constants
const MS_PER_DAY = 24 * 3600 * 1000
const OFFSET = MS_PER_DAY / 8
const todayUTC = Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
const startUTC = todayUTC - 29 * MS_PER_DAY
const tickPositions = Array.from({length: 30}, (_, i) => startUTC + i * MS_PER_DAY)
const dayMidpoints = tickPositions.slice(0, -1).map((pos) => pos + MS_PER_DAY / 2)
const MIN = startUTC - OFFSET
const MAX = todayUTC + OFFSET

const BloodPressureChart: React.FC<BloodPressureChartProps> = ({data}) => {
  const {currentUser} = useReduxAuth()
  const {patientId} = useParams<{patientId: string}>()

  const [thresholds, setThresholds] = useState<Thresholds | null>(null)

  // Fetch Stage-2 thresholds (severity=5) for sys & dia
  useEffect(() => {
    const load = async () => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        // TODO::Staff
        const isNurseParam = currentUser?.isNurse ? '&is_nurse=1' : ''
        const qp = `timezone=${encodeURIComponent(tz)}${isNurseParam}`

        const resp = await axios.get(
          API_ENDPOINTS.GET_ALL_PATIENT_CRITICALITY_SETTINGS(patientId!, qp)
        )
        const criticalSettings = resp.data?.data?.allPatientSettings;
        const all = criticalSettings.length > 0 ? criticalSettings : helperFunctions.getDefautSettings(patientId)
        const sys2 = all.find(
          (s: any) => s.TelemetryType.name === 'systolic_pressure' && s.severity === '5'
        )
        const dia2 = all.find(
          (s: any) => s.TelemetryType.name === 'diastolic_pressure' && s.severity === '5'
        )
        if (sys2 && dia2) {
          setThresholds({
            sysMin: sys2.min_value,
            sysMax: sys2.max_value,
            diaMin: dia2.min_value,
            diaMax: dia2.max_value,
          })
        }
      } catch (err) {
        console.error('Could not load thresholds', err)
      }
    }
    load()
  }, [currentUser, patientId])

  // Build dumbbell series
  const seriesData = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const buckets = new Map<number, Reading[]>()
    data.forEach((r) => {
      const d = new Date(r.reading_taken_at)
      const day = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
      if (day < startUTC || day > todayUTC) return
      const arr = buckets.get(day) || []
      arr.push(r)
      buckets.set(day, arr)
    })

    const makeSeries = (key: 'sys' | 'dia' | 'pulse', shift: number) =>
      Array.from(buckets, ([day, arr]) => ({
        x: day + shift,
        low: Math.min(...arr.map((r) => r[key])),
        high: Math.max(...arr.map((r) => r[key])),
        readings: arr,
        bucketDay: day,
      }))

    return [
      {
        type: 'dumbbell',
        name: 'Systolic BP',
        data: makeSeries('sys', -OFFSET),
        color: '#d32f2f',
        lowColor: '#d32f2f',
        connectorWidth: 4,
        connectorColor: 'rgba(211,47,47,0.4)',
        dashStyle: 'Solid',
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          fillColor: '#d32f2f',
          lineColor: '#d32f2f',
        },
        lowMarker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          fillColor: '#d32f2f',
          lineColor: '#d32f2f',
        },
        states: {inactive: {enabled: false}},
      },
      {
        type: 'dumbbell',
        name: 'Diastolic BP',
        data: makeSeries('dia', -OFFSET),
        color: '#1976d2',
        lowColor: '#1976d2',
        connectorWidth: 4,
        connectorColor: 'rgba(25,118,210,0.4)',
        dashStyle: 'Solid',
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          fillColor: '#1976d2',
          lineColor: '#1976d2',
        },
        lowMarker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          fillColor: '#1976d2',
          lineColor: '#1976d2',
        },
        states: {inactive: {enabled: false}},
      },
      {
        type: 'dumbbell',
        name: 'Pulse',
        data: makeSeries('pulse', OFFSET),
        color: '#ff9800',
        lowColor: '#ff9800',
        connectorWidth: 4,
        connectorColor: 'rgba(255,152,0,0.4)',
        dashStyle: 'Solid',
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          fillColor: '#ff9800',
          lineColor: '#ff9800',
        },
        lowMarker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          fillColor: '#ff9800',
          lineColor: '#ff9800',
        },
        states: {inactive: {enabled: false}},
      },
    ]
  }, [data])

  // Calculate dynamic min/max based on data, with 40-220 as default range
  const {chartMin, chartMax} = useMemo(() => {
    if (!data.length) return {chartMin: 40, chartMax: 220}
    
    const allValues = data.flatMap(reading => [reading.sys, reading.dia, reading.pulse])
    const dataMin = Math.min(...allValues)
    const dataMax = Math.max(...allValues)
    
    // Use 40-220 as default, but expand if data goes outside this range
    let chartMin = 40
    let chartMax = 220
    
    // Only expand if data actually goes outside the 40-220 range
    if (dataMin < 40) {
      const bufferedMin = dataMin - 10
      // Round down to nearest 10, but don't go below 10
      chartMin = Math.max(10, Math.floor(bufferedMin / 10) * 10)
    }
    
    if (dataMax > 220) {
      const bufferedMax = dataMax + 10
      // Round up to nearest 10
      chartMax = Math.ceil(bufferedMax / 10) * 10
    }    
   
    return {chartMin, chartMax}
  }, [data])

  const options: Highcharts.Options = {
    chart: {
      type: 'dumbbell',
      spacingLeft: 10,
      spacingRight: 10,
      marginLeft: 60,
      marginRight: 60,
    },
    title: {text: ''}, // ← ensure no title
    credits: {enabled: false},
    plotOptions: {
      series: {clip: false},
      dumbbell: {turboThreshold: 0, cropThreshold: 0, clip: false},
    },
    xAxis: {
      type: 'datetime',
      min: MIN,
      max: MAX,
      tickPositions,
      labels: {format: '{value:%e %b}', rotation: -45},
      tickLength: 0,
      plotLines: dayMidpoints.map((v) => ({
        value: v,
        color: '#eee',
        width: 1,
        dashStyle: 'Dash',
        zIndex: 2,
      })),
    },
    yAxis: [
      {
        // Left y-axis
        title: {text: 'mmHg / Pulse (bpm)'},
        min: chartMin,
        max: chartMax,
        startOnTick: false,
        endOnTick: false,
        tickInterval: 20,
        gridLineWidth: 0,
        plotLines: thresholds
          ? [
              {
                value: thresholds.sysMin,
                color: '#d32f2f',
                dashStyle: 'Dash',
                width: 2,
                label: {
                  text: `Sys (${thresholds.sysMin})`,
                  align: 'right',
                  x: -10,
                  style: {color: '#d32f2f', fontWeight: 'bold'},
                },
              },
              {
                value: thresholds.diaMin,
                color: '#1976d2',
                dashStyle: 'Dash',
                width: 2,
                label: {
                  text: `Dia (${thresholds.diaMin})`,
                  align: 'right',
                  x: -10,
                  style: {color: '#1976d2', fontWeight: 'bold'},
                },
              },
            ]
          : [],
      },
      {
        // Right y-axis (mirror of left)
        title: {text: 'mmHg / Pulse (bpm)'},
        min: chartMin,
        max: chartMax,
        startOnTick: false,
        endOnTick: false,
        tickInterval: 20,
        opposite: true,
        gridLineWidth: 0,
        plotLines: thresholds
          ? [
              {
                value: thresholds.sysMin,
                color: '#d32f2f',
                dashStyle: 'Dash',
                width: 2,
                label: {
                  text: `Sys (${thresholds.sysMin})`,
                  align: 'left',
                  x: 10,
                  style: {color: '#d32f2f', fontWeight: 'bold'},
                },
              },
              {
                value: thresholds.diaMin,
                color: '#1976d2',
                dashStyle: 'Dash',
                width: 2,
                label: {
                  text: `Dia (${thresholds.diaMin})`,
                  align: 'left',
                  x: 10,
                  style: {color: '#1976d2', fontWeight: 'bold'},
                },
              },
            ]
          : [],
      },
    ],
    series: seriesData,
    tooltip: {useHTML: true, formatter: tooltipFormatter},
  }

  return (
    <div className='chart-style'>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  )
}

export default BloodPressureChart
